import { RcTrans } from '@/components/atoms/RcTrans'
import { Calendar } from '@/components/organisms/calendar/Calendar'
import { PageForm } from '@/components/organisms/page/PageForm'
import { useTime } from '@/core/hooks/useTime'
import { useUserSchedules } from '@/entity/user/useUserSchedules'
import Stack from '@mui/material/Stack'
import {
  CompetitionEventCreateCommand,
  CompetitionEventUpdateCommand,
  DayOfWeek
} from '@rallycry/api-suite-typescript/dist/models'
import moment from 'moment-timezone'
import { useEffect, useMemo } from 'react'

// Convert from DayOfWeek enum to numeric day (0-6) for moment.js
const getDayNumberFromEnum = (day: DayOfWeek): number => {
  switch (day) {
    case DayOfWeek.SUNDAY:
      return 0
    case DayOfWeek.MONDAY:
      return 1
    case DayOfWeek.TUESDAY:
      return 2
    case DayOfWeek.WEDNESDAY:
      return 3
    case DayOfWeek.THURSDAY:
      return 4
    case DayOfWeek.FRIDAY:
      return 5
    case DayOfWeek.SATURDAY:
      return 6
    default:
      throw new Error('Invalid day of week')
  }
}

export const SchedulePreferences = () => {
  const { schedules, remove, update, create } = useUserSchedules()
  const { getNow } = useTime()

  // Store current day of week to use for calculations
  const currentDayOfWeek = useMemo(() => getNow().day(), [getNow])

  useEffect(() => {
    // Configure moment locale to make the first day of week be today
    const currentLocale = moment.locale()
    const newDow = currentDayOfWeek
    const prevDow = moment.localeData(currentLocale).firstDayOfWeek()
    moment.locale(currentLocale, { week: { dow: newDow } })

    // Reset the locale to default after configuration
    return () => {
      moment.updateLocale(currentLocale, { week: { dow: prevDow } })
    }
  }, [currentDayOfWeek])

  const events = useMemo(
    () =>
      schedules.map(it => {
        // Convert API's day of week to numeric day (0-6)
        const scheduleDayNum = getDayNumberFromEnum(it.byDay!)

        // Create a base date starting at the beginning of the current week
        const baseDate = moment().startOf('week')

        // Check if this day should wrap to next week (is before the current day in the natural order)
        // When scheduleDayNum < currentDayOfWeek, it means the day is "behind" the first day of our custom week
        const isNextWeek = scheduleDayNum < currentDayOfWeek

        // Create start and end dates, adding 7 days if the event belongs to next week
        const startDate = baseDate
          .clone()
          .day(scheduleDayNum)
          .add(isNextWeek ? 7 : 0, 'days')
          .set({
            hour: moment(it.startTime, 'HH:mm:ss').hour(),
            minute: moment(it.startTime, 'HH:mm:ss').minute()
          })
          .toDate()

        const endDate = baseDate
          .clone()
          .day(scheduleDayNum)
          .add(isNextWeek ? 7 : 0, 'days')
          .set({
            hour: moment(it.endTime, 'HH:mm:ss').hour(),
            minute: moment(it.endTime, 'HH:mm:ss').minute()
          })
          .toDate()

        return {
          id: it.id,
          title: `${it.name}${it.description ? ` - ${it.description}` : ''}`,
          startDate,
          endDate
        }
      }),
    [schedules, currentDayOfWeek]
  )

  const handleCreate = async (
    cmd: CompetitionEventCreateCommand
  ): Promise<void> => {
    const start = moment(cmd.startDate)
    const end = moment(cmd.endDate)

    await create({
      byDay: getDayOfWeek(start.day()),
      startTime: start.format('HH:mm:ss'),
      endTime: end.format('HH:mm:ss'),
      scheduleTimeZone: moment.tz.guess()
    })
  }

  const handleUpdate = async (
    id: number,
    cmd: CompetitionEventUpdateCommand
  ): Promise<void> => {
    const start = moment(cmd.startDate)
    const end = moment(cmd.endDate)
    await update(id, {
      byDay: getDayOfWeek(start.day()),
      startTime: start.format('HH:mm:ss'),
      endTime: end.format('HH:mm:ss'),
      scheduleTimeZone: moment.tz.guess()
    })
  }

  const getDayOfWeek = (day: number): DayOfWeek => {
    switch (day) {
      case 0:
        return DayOfWeek.SUNDAY
      case 1:
        return DayOfWeek.MONDAY
      case 2:
        return DayOfWeek.TUESDAY
      case 3:
        return DayOfWeek.WEDNESDAY
      case 4:
        return DayOfWeek.THURSDAY
      case 5:
        return DayOfWeek.FRIDAY
      case 6:
        return DayOfWeek.SATURDAY
      default:
        throw new Error('Invalid day of week')
    }
  }

  return (
    <Stack direction='column' spacing={5} justifyContent={'flex-start'}>
      {/* <PageForm
        title={<RcTrans i18nKey='profile:timezone-preferences-title' />}
      >
        <TimezonePreference />
      </PageForm> */}

      <PageForm
        title={<RcTrans i18nKey='profile:schedule-preferences-title' />}
        description={
          <RcTrans i18nKey='profile:schedule-preferences-description' />
        }
      >
        <Stack direction='column' spacing={5} justifyContent={'flex-start'}>
          <Calendar
            height={600}
            editable
            disableNavigation
            views={['week']}
            events={events}
            simpleMode
            padding={5}
            create={handleCreate}
            update={handleUpdate}
            remove={remove}
          />
        </Stack>
      </PageForm>
    </Stack>
  )
}
